// Train Roof Dash (level 10) — a forward race across the rooftops of a moving
// train. Flat boxcar roofs (support meshes) separated by gaps the player must
// jump, with swinging signs that sweep across the roof (jump them), low-tunnel
// arches with a jumpable crate, smoke clouds (visibility), and start/finish
// stations. The course layout (cars / gates / signs / tunnels / smoke) lives on
// Path.trainRoof; this module builds the 3D + the collision query.
import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { makeCrowdFan } from "./World";

const ROOF_TOP_Y = 0.18;        // walkable roof height (matches Path.heightAt)
const ROOF_THICK = 0.4;
const SIGN_Y = 0.95;            // sweeping-sign bar height — low + jumpable
const SIGN_VEL = 1.15;          // rad/s sweep
const CRATE_Y = 0.55;           // jumpable crate-bar height at a tunnel
const BOB_SPEED = 0.62;         // rad/s — how fast the lanes rise + fall
// One solid colour per lane (left / centre / right) — three distinct trains.
const LANE_COLORS = [0xd83b3b, 0x2f7fe0, 0x2eb866];
const STATION_COLOR = 0xb8772f;

export interface TrainRoofHandle {
  group: THREE.Group;
  /** Roof meshes the standable-surface raycaster lands the player on. */
  supportMeshes: THREE.Object3D[];
  /** Challenge-gate s-positions for the Spawner. */
  gatePositions: number[];
  /** Spin the swinging signs + drift the smoke each frame. */
  tick: (clockMs: number) => void;
  /** Swinging-sign / crate contact for the player at (s, l). Same shape as the
   *  circle-spinner so Game can reuse applyBarImpact. Both are LOW + jumpable
   *  (Game skips the hit while the player is airborne). */
  barContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
  } | null;
}

interface SignBar {
  pivot: THREE.Group;
  s: number;
  angle: number;
  vel: number;
  halfLen: number;
  thickness: number;
}
interface CrateBar {
  s: number;
  halfLen: number;   // lateral half-extent
  halfDepth: number; // along-track half-extent
}

export function buildTrainRoofCourse(path: Path): TrainRoofHandle {
  const tr = path.trainRoof;
  if (!tr) throw new Error("buildTrainRoofCourse called on a non train-roof path");
  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const hw = tr.halfWidth;

  const railMat = new THREE.MeshStandardMaterial({ color: 0x6b7280, roughness: 0.7, metalness: 0.3 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0xeef2f6, roughness: 0.55, metalness: 0.06 });
  const windowMat = new THREE.MeshStandardMaterial({ color: 0x9fd8ff, roughness: 0.2, metalness: 0.1, emissive: 0x1b3a5b, emissiveIntensity: 0.15 });

  // ── Three lanes of boxcars. Each lane lives in its own group so we can bob
  //    the whole train up and down in tick(); the player rides whichever roof
  //    is under their feet (raycast support follows the moving meshes).
  const laneGroups: { group: THREE.Group; phase: number; amp: number }[] = [];
  const centerLaneGroup = new THREE.Group(); // holds signs/tunnels so they bob with the centre train
  tr.lanes.forEach((lane, li) => {
    const laneGroup = new THREE.Group();
    laneGroup.position.x = lane.offset;
    group.add(laneGroup);
    laneGroups.push({ group: laneGroup, phase: lane.phase, amp: lane.amp });
    const laneColor = LANE_COLORS[li % LANE_COLORS.length]!;
    const isCenter = Math.abs(lane.offset) < 0.01;
    if (isCenter) { centerLaneGroup.position.x = lane.offset; laneGroup.add(centerLaneGroup); }

    tr.cars.forEach((car, i) => {
      const len = Math.max(1, car.end - car.start);
      const midZ = (car.start + car.end) / 2;
      const isStation = i === 0 || i === tr.cars.length - 1;
      const color = isStation ? STATION_COLOR : laneColor;

      const roof = new THREE.Mesh(new SoftBox(hw * 2, ROOF_THICK, len - 0.3), roofMat);
      roof.position.set(0, ROOF_TOP_Y - ROOF_THICK / 2, midZ);
      roof.castShadow = true;
      roof.receiveShadow = true;
      laneGroup.add(roof);
      supportMeshes.push(roof);

      const body = new THREE.Mesh(
        new SoftBox(hw * 2 + 0.4, 2.6, len - 0.15),
        new THREE.MeshStandardMaterial({ color, roughness: 0.42, metalness: 0.12 }),
      );
      body.position.set(0, ROOF_TOP_Y - ROOF_THICK - 1.3, midZ);
      body.castShadow = true;
      laneGroup.add(body);

      for (const side of [-1, 1] as const) {
        const strip = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.6, len - 1.2), windowMat);
        strip.position.set(side * (hw + 0.18), ROOF_TOP_Y - ROOF_THICK - 0.9, midZ);
        laneGroup.add(strip);
      }
      for (const side of [-1, 1] as const) {
        for (const wz of [midZ - len * 0.3, midZ + len * 0.3]) {
          const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.42, 0.42, 0.28, 14), railMat);
          wheel.rotation.z = Math.PI / 2;
          wheel.position.set(side * (hw + 0.05), ROOF_TOP_Y - ROOF_THICK - 2.55, wz);
          laneGroup.add(wheel);
        }
      }
    });
  });

  // ── Swinging signs — a sweeping bar on a gantry. Low enough to jump.
  const signs: SignBar[] = [];
  tr.signs.forEach((s, i) => {
    const pivot = new THREE.Group();
    pivot.position.set(0, ROOF_TOP_Y + SIGN_Y, s);
    const halfLen = hw - 0.6;
    const thickness = 0.32;
    // Gantry posts.
    for (const side of [-1, 1] as const) {
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, SIGN_Y + 1.6, 12), railMat);
      post.position.set(side * (hw + 0.2), ROOF_TOP_Y + (SIGN_Y + 1.6) / 2, s);
      centerLaneGroup.add(post);
    }
    // Sweeping arm + sign board.
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(thickness, halfLen * 2, 8, 16),
      new THREE.MeshStandardMaterial({ color: 0xffb300, emissive: 0xffb300, emissiveIntensity: 0.12, roughness: 0.4 }),
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    pivot.add(arm);
    const board = new THREE.Mesh(
      new SoftBox(1.5, 0.95, 0.14),
      new THREE.MeshStandardMaterial({ color: 0xe53935, roughness: 0.45 }),
    );
    board.position.set(halfLen * 0.6, 0, 0);
    pivot.add(board);
    centerLaneGroup.add(pivot);
    signs.push({ pivot, s, angle: i * 0.8, vel: i % 2 === 0 ? SIGN_VEL : -SIGN_VEL, halfLen, thickness });
  });

  // ── Low tunnels: an arch over the car + a jumpable crate just before it.
  const crates: CrateBar[] = [];
  tr.tunnels.forEach((s) => {
    // Arch frame (visual).
    const archMat = new THREE.MeshStandardMaterial({ color: 0x5b6470, roughness: 0.6, metalness: 0.2 });
    const top = new THREE.Mesh(new SoftBox(hw * 2 + 1.2, 0.6, 1.6), archMat);
    top.position.set(0, ROOF_TOP_Y + 2.6, s);
    centerLaneGroup.add(top);
    for (const side of [-1, 1] as const) {
      const leg = new THREE.Mesh(new SoftBox(0.6, 2.9, 1.6), archMat);
      leg.position.set(side * (hw + 0.6), ROOF_TOP_Y + 1.45, s);
      centerLaneGroup.add(leg);
    }
    // Jumpable crate just before the arch.
    const crateS = s - 2.6;
    const crate = new THREE.Mesh(
      new SoftBox(2.0, CRATE_Y * 2, 1.4),
      new THREE.MeshStandardMaterial({ color: 0xb07a3c, roughness: 0.7 }),
    );
    crate.position.set(0, ROOF_TOP_Y + CRATE_Y, crateS);
    crate.castShadow = true;
    centerLaneGroup.add(crate);
    crates.push({ s: crateS, halfLen: 1.0, halfDepth: 0.7 });
  });

  // ── Smoke clouds (visibility flavour) — soft drifting puffs over a car.
  const puffs: THREE.Mesh[] = [];
  const puffMat = new THREE.MeshBasicMaterial({ color: 0xd9dde2, transparent: true, opacity: 0.55, depthWrite: false });
  tr.smoke.forEach((s) => {
    for (let k = 0; k < 7; k++) {
      const r = 1.1 + Math.random() * 0.9;
      const puff = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8), puffMat);
      puff.position.set((Math.random() - 0.5) * hw * 2, ROOF_TOP_Y + 1.4 + Math.random() * 1.6, s + (Math.random() - 0.5) * 6);
      puff.userData.decorOnly = true;
      group.add(puff);
      puffs.push(puff);
    }
  });
  // A smoke stack on the lead car so the clouds have a source.
  const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.6, 1.2, 16), new THREE.MeshStandardMaterial({ color: 0x37474f, roughness: 0.6 }));
  stack.position.set(0, ROOF_TOP_Y + 0.6, tr.smoke[0] ?? tr.cars[1]?.start ?? 30);
  centerLaneGroup.add(stack);

  // ── Cheering crowd along the embankment (decor; not a support).
  for (let i = 0; i < 18; i++) {
    const z = 20 + (i / 18) * (path.length - 40);
    const side = i % 2 === 0 ? -1 : 1;
    const fan = makeCrowdFan(i);
    fan.position.set(side * (hw + 6.5), ROOF_TOP_Y - ROOF_THICK - 2.0, z);
    fan.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
    group.add(fan);
  }

  // ── Per-frame: bob each lane up + down (out of phase), sweep the signs,
  //    drift the smoke. The player rides whichever roof is under them because
  //    the standable-surface raycast follows the moving meshes.
  let lastClock = 0;
  const tick = (clockMs: number) => {
    const dt = lastClock === 0 ? 0.016 : Math.max(0, Math.min(0.064, (clockMs - lastClock) / 1000));
    lastClock = clockMs;
    const t = clockMs * 0.001;
    for (const lane of laneGroups) {
      lane.group.position.y = Math.sin(t * BOB_SPEED + lane.phase) * lane.amp;
    }
    for (const sign of signs) {
      sign.angle += sign.vel * dt;
      sign.pivot.rotation.y = sign.angle;
    }
    for (const puff of puffs) {
      puff.position.x += Math.sin(clockMs * 0.0004 + puff.position.z) * 0.004;
    }
  };

  // ── Collision: swinging signs (rotating bars) + crates (static bars).
  const barRect = (
    ds: number, dl: number, angle: number, halfLen: number, halfThick: number, radius: number, angularVel: number,
  ) => {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const localX = dl * cos - ds * sin;
    const localY = dl * sin + ds * cos;
    const clampedX = Math.max(-halfLen, Math.min(halfLen, localX));
    const dx = localX - clampedX;
    const dy = localY;
    const sumR = halfThick + radius;
    const dist2 = dx * dx + dy * dy;
    if (dist2 >= sumR * sumR) return null;
    const dist = Math.sqrt(Math.max(0.0001, dist2));
    const nx = dist > 0.001 ? dx / dist : 0;
    const ny = dist > 0.001 ? dy / dist : 1;
    return {
      normalL: nx * cos + ny * sin,
      normalS: -nx * sin + ny * cos,
      penetration: sumR - dist,
      barVelL: angularVel * ds,
      barVelS: -angularVel * dl,
    };
  };

  const barContact = (s: number, l: number, radius: number) => {
    for (const sign of signs) {
      const ds = s - sign.s;
      if (Math.abs(ds) > sign.halfLen + radius + 0.5) continue;
      const hit = barRect(ds, l, sign.angle, sign.halfLen, sign.thickness, radius, sign.vel);
      if (hit) return hit;
    }
    for (const crate of crates) {
      const ds = s - crate.s;
      if (Math.abs(ds) > crate.halfDepth + radius && Math.abs(l) > crate.halfLen + radius) continue;
      // Axis-aligned static box (no rotation) → reuse barRect with angle 0,
      // treating it as a wide bar centred on the track.
      const hit = barRect(ds, l, 0, crate.halfLen, crate.halfDepth + 0.1, radius, 0);
      if (hit) return hit;
    }
    return null;
  };

  return { group, supportMeshes, gatePositions: tr.gates, tick, barContact };
}
